// ═══════════════════════════════════════════════════════════════════════════════
// Home Stock category badge palette — matches TaskReviewPage inventory v2 item cards.
// Preferred color from string hash; collisions resolved in task category list order.
// ═══════════════════════════════════════════════════════════════════════════════

const INVENTORY_V2_CATEGORY_BADGE_STYLES = [
  'border-emerald-200/90 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/45 dark:text-emerald-100',
  'border-sky-200/90 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-950/45 dark:text-sky-100',
  'border-violet-200/90 bg-violet-50 text-violet-900 dark:border-violet-800 dark:bg-violet-950/45 dark:text-violet-100',
  'border-amber-200/90 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/45 dark:text-amber-100',
  'border-rose-200/90 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950/45 dark:text-rose-100',
  'border-cyan-200/90 bg-cyan-50 text-cyan-900 dark:border-cyan-800 dark:bg-cyan-950/45 dark:text-cyan-100',
  'border-indigo-200/90 bg-indigo-50 text-indigo-900 dark:border-indigo-800 dark:bg-indigo-950/45 dark:text-indigo-100',
  'border-fuchsia-200/90 bg-fuchsia-50 text-fuchsia-900 dark:border-fuchsia-800 dark:bg-fuchsia-950/45 dark:text-fuchsia-100',
] as const;

const PALETTE_LENGTH = INVENTORY_V2_CATEGORY_BADGE_STYLES.length;

const hashCategoryLabel = (categoryLabel: string): number => {
  let h = 0;
  for (let i = 0; i < categoryLabel.length; i += 1) {
    h = (h * 31 + categoryLabel.charCodeAt(i)) >>> 0;
  }
  return h;
};

/** Hash-preferred palette index (0–7) for a category label. */
export const inventoryV2CategoryBadgePreferredIndex = (categoryLabel: string): number =>
  hashCategoryLabel(categoryLabel) % PALETTE_LENGTH;

const findFirstUnusedPaletteIndex = (used: ReadonlySet<number>): number | null => {
  for (let i = 0; i < PALETTE_LENGTH; i += 1) {
    if (!used.has(i)) {
      return i;
    }
  }
  return null;
};

/**
 * Assigns distinct palette indices for task-ordered categories.
 * Uses hash as first choice; on collision picks the lowest unused index (0–7).
 */
export const buildInventoryV2CategoryBadgeIndexMap = (
  orderedCategories: readonly string[],
): ReadonlyMap<string, number> => {
  const used = new Set<number>();
  const map = new Map<string, number>();
  const seen = new Set<string>();

  for (const raw of orderedCategories) {
    const label = raw.trim();
    if (label === '' || seen.has(label)) {
      continue;
    }
    seen.add(label);
    const preferred = inventoryV2CategoryBadgePreferredIndex(label);
    if (!used.has(preferred)) {
      map.set(label, preferred);
      used.add(preferred);
      continue;
    }
    const fallback = findFirstUnusedPaletteIndex(used);
    const resolved = fallback ?? preferred % PALETTE_LENGTH;
    map.set(label, resolved);
    if (fallback !== null) {
      used.add(resolved);
    }
  }
  return map;
};

export const inventoryV2CategoryBadgeClassNameByIndex = (index: number): string => {
  const normalized = ((index % PALETTE_LENGTH) + PALETTE_LENGTH) % PALETTE_LENGTH;
  return INVENTORY_V2_CATEGORY_BADGE_STYLES[normalized];
};

/** Tailwind classes for a category badge; optional map from buildInventoryV2CategoryBadgeIndexMap. */
export const inventoryV2CategoryBadgeClassName = (
  categoryLabel: string,
  indexMap?: ReadonlyMap<string, number>,
): string => {
  const trimmed = categoryLabel.trim();
  if (trimmed === '') {
    return inventoryV2CategoryBadgeClassNameByIndex(0);
  }
  const fromMap = indexMap?.get(trimmed);
  const index =
    fromMap !== undefined ? fromMap : inventoryV2CategoryBadgePreferredIndex(trimmed);
  return inventoryV2CategoryBadgeClassNameByIndex(index);
};
